import {
    type ReactElement,
    isValidElement,
    type ReactNode,
    Children,
    Fragment
} from "react";
import type {
    ViewStyle
} from "react-native";
import type {
    AttachedGroupSquircleCornersConstantType,
    AttachedGroupLayerCornersConstantType,
    AttachedGroupOuterRadiusConstantType,
    AttachedGroupItemStyleConstantType,
    AttachedGroupPositionConstantType,
    AttachedGroupFlattenConstantType,
    AttachedGroupCornersConstantType,
    AttachedGroupFlattened,
    AttachedGroupPosition,
    AttachedGroupCorners
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";

export const getAttachedGroupZIndexes = ({
    layers
}: {
    layers: NCoreUIKit.ShapeConfigs["layers"];
}) => {
    return {
        focus: layers.raised + 2,
        disabled: layers.base,
        on: layers.raised + 1,
        rest: layers.raised
    };
};

export const ATTACHED_GROUP_START_POSITIONS: Array<AttachedGroupPosition> = [
    "first",
    "only"
];

export const ATTACHED_GROUP_END_POSITIONS: Array<AttachedGroupPosition> = [
    "last",
    "only"
];

export const flattenAttachedGroupChildren = ({
    children
}: AttachedGroupFlattenConstantType): AttachedGroupFlattened => {
    const elements: AttachedGroupFlattened = [];

    const visit = (node: ReactNode) => {
        Children.forEach(node, (child) => {
            if(!isValidElement<{
                children?: ReactNode;
            }>(child)) {
                return;
            }

            if(child.type === Fragment) {
                visit(child.props.children);

                return;
            }

            elements.push(child as ReactElement);
        });
    };

    visit(children);

    return elements;
};

export const getAttachedGroupPosition = ({
    index,
    count
}: AttachedGroupPositionConstantType): AttachedGroupPosition => {
    if(count <= 1) {
        return "only";
    }

    if(index === 0) {
        return "first";
    }

    if(index === count - 1) {
        return "last";
    }

    return "middle";
};

export const getAttachedGroupOuterRadius = ({
    orientation,
    minHeight,
    radiuses
}: AttachedGroupOuterRadiusConstantType): number => {
    const maxRadius = minHeight / 2;

    if(orientation === "vertical") {
        return Math.min(radiuses.actions, radiuses.form, maxRadius);
    }

    return Math.min(radiuses.actions, maxRadius);
};

export const getAttachedGroupCorners = ({
    innerRadius,
    orientation,
    outerRadius,
    position
}: AttachedGroupCornersConstantType): AttachedGroupCorners => {
    const isStart = ATTACHED_GROUP_START_POSITIONS.includes(position);

    const isEnd = ATTACHED_GROUP_END_POSITIONS.includes(position);

    const startRadius = isStart ? outerRadius : innerRadius;

    const endRadius = isEnd ? outerRadius : innerRadius;

    if(orientation === "vertical") {
        return {
            borderBottomStartRadius: endRadius,
            borderTopStartRadius: startRadius,
            borderBottomEndRadius: endRadius,
            borderTopEndRadius: startRadius
        };
    }

    return {
        borderBottomStartRadius: startRadius,
        borderTopStartRadius: startRadius,
        borderBottomEndRadius: endRadius,
        borderTopEndRadius: endRadius
    };
};

export const getAttachedGroupSquircleCorners = ({
    shapeConfigs,
    corners
}: AttachedGroupSquircleCornersConstantType): ViewStyle => {
    const cornerKeys = Object.keys(corners) as Array<keyof AttachedGroupCorners>;

    const styles = {} as Mutable<ViewStyle>;

    cornerKeys.forEach((cornerKey) => {
        const {
            borderRadius,
            ...shape
        } = getCornerStyle({
            radius: corners[cornerKey],
            shapeConfigs
        });

        styles[cornerKey] = borderRadius;

        if(corners[cornerKey] > 0) {
            Object.assign(styles, shape);
        }
    });

    return styles;
};

export const getAttachedGroupItemStyle = ({
    shapeConfigs,
    innerRadius,
    orientation,
    outerRadius,
    isDisabled,
    isFocused,
    isOverlap,
    isActive,
    position,
    borders
}: AttachedGroupItemStyleConstantType) => {
    const zIndexes = getAttachedGroupZIndexes({
        layers: shapeConfigs.layers
    });

    const styles = {
        surface: getAttachedGroupSquircleCorners({
            corners: getAttachedGroupCorners({
                innerRadius,
                outerRadius,
                orientation,
                position
            }),
            shapeConfigs
        }) as Mutable<ViewStyle>,
        container: {
            zIndex: zIndexes.rest
        } as Mutable<ViewStyle>
    };

    if(isActive) {
        styles.container.zIndex = zIndexes.on;
    }

    if(isDisabled) {
        styles.container.zIndex = zIndexes.disabled;
    }

    if(isFocused && !isDisabled) {
        styles.container.zIndex = zIndexes.focus;
    }

    if(isOverlap && position !== "first" && position !== "only") {
        if(orientation === "vertical") {
            styles.container.marginTop = -borders.line;
        } else {
            styles.container.marginStart = -borders.line;
        }
    }

    return styles;
};

export const getAttachedGroupLayerCorners = ({
    corners,
    borders
}: AttachedGroupLayerCornersConstantType): AttachedGroupCorners => {
    return {
        borderBottomStartRadius: concentric({
            outer: corners.borderBottomStartRadius,
            role: corners.borderBottomStartRadius,
            inset: borders.line
        }),
        borderBottomEndRadius: concentric({
            outer: corners.borderBottomEndRadius,
            role: corners.borderBottomEndRadius,
            inset: borders.line
        }),
        borderTopStartRadius: concentric({
            outer: corners.borderTopStartRadius,
            role: corners.borderTopStartRadius,
            inset: borders.line
        }),
        borderTopEndRadius: concentric({
            outer: corners.borderTopEndRadius,
            role: corners.borderTopEndRadius,
            inset: borders.line
        })
    };
};
